/**
 * tRPC Client Configuration for Monutrack
 *
 * This module provides a configured tRPC client with Amplify IAM authentication.
 * It uses a custom fetch implementation that routes requests through Amplify's
 * API client for automatic IAM signing.
 *
 * @see docs/research/trpc/frontend/src/trpc.ts - Reference implementation
 */

import type { AppRouter } from '@ds/ds-api-types/types'
import type { QueryClient } from '@tanstack/react-query'
import { httpLink, type TRPCClient } from '@trpc/client'
import { createTRPCQueryUtils, createTRPCReact } from '@trpc/react-query'
import { get, post } from 'aws-amplify/api'

/**
 * Create typed tRPC React hooks
 * This provides full TypeScript inference from the backend's AppRouter type
 */
export const trpc = createTRPCReact<AppRouter>()

/**
 * Progress Router Hooks
 *
 * The progress router is deployed to the backend but not yet published
 * in the @ds/ds-api-types package. These typed hooks provide access to
 * progress endpoints with proper TypeScript inference.
 *
 * Usage:
 *   import { useGlobalProgress, useMyClientProgress } from '@/api/trpc'
 *   const { data, isLoading } = useGlobalProgress({ year: 2025 })
 */
export { useGlobalProgress, useMyClientProgress, useClientProgress } from './progress-hooks'

/**
 * Custom fetch function that uses Amplify's API client with automatic IAM signing
 *
 * Key features:
 * - Routes requests through Amplify's `get`/`post` methods
 * - Automatic IAM signature for API Gateway authorization
 * - Transforms tRPC procedure paths to REST-style paths
 *
 * @param url - The URL to fetch
 * @param options - Fetch options including method and body
 * @returns Response object compatible with fetch API
 */
async function customFetch(url: RequestInfo | URL, options?: RequestInit): Promise<Response> {
  try {
    const urlString = typeof url === 'string' ? url : url instanceof URL ? url.toString() : url.url
    const method = options?.method || 'GET'
  // Use window.location.origin as base for relative URLs to avoid "Invalid URL" errors
  const urlObj = new URL(urlString, window.location.origin)
  let path = urlObj.pathname + urlObj.search
  // Extract just the path from the full URL

  // Transform tRPC procedure paths to REST-style paths
  // e.g., /progress.getGlobalProgress?... -> /progress/getGlobalProgress?...
  // This allows separate Lambda handlers per namespace
  path = path.replace(/\/([^/?]+)\.([^/?]+)/, '/$1/$2')

  // Use Amplify's API methods which automatically handle IAM signing
  const parsedBody: unknown = options?.body ? JSON.parse(options.body as string) : undefined
  const apiOperation = method === 'GET'
    ? get({ apiName: 'monumenta-trpc', path })
    : post({
        apiName: 'monumenta-trpc',
        path,
        options: {
          // Amplify expects DocumentType but tRPC sends JSON - cast to satisfy types
          body: parsedBody as Record<string, unknown>,
        } as Parameters<typeof post>[0]['options'],
      })

  const response = await apiOperation.response

  // Convert Amplify response to fetch Response
  const body = await response.body.text()

  return new Response(body, {
    status: response.statusCode,
      headers: response.headers as HeadersInit,
    })
  } catch (error) {
    throw error
  }
}

/**
 * Create a tRPC client instance configured for the given API URL
 *
 * Uses httpLink (not batched) because our backend architecture deploys
 * each tRPC procedure to its own Lambda function. Batching would attempt
 * to send multiple procedure calls in one request, which doesn't work
 * with this setup.
 *
 * @param apiUrl - The base URL for the tRPC API
 * @returns Configured tRPC client
 */
export function createTRPCClient(apiUrl: string): ReturnType<typeof trpc.createClient> {
  return trpc.createClient({
    links: [
      httpLink({
        url: apiUrl,
        fetch: customFetch,
      }),
    ],
  })
}

// Re-export createTRPCQueryUtils for use in route loaders
export { createTRPCQueryUtils }

/**
 * Helper to create query utils for prefetching in route loaders
 *
 * @param client - The tRPC client instance
 * @param queryClient - The React Query client instance
 * @returns Query utils for prefetching
 */
export function createTRPCQueryUtilsFromClient(
  client: TRPCClient<AppRouter>,
  queryClient: QueryClient
) {
  return createTRPCQueryUtils({ client, queryClient })
}
